import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  bookingServicesApi,
  type CreateBookingServicePayload,
  type UpdateBookingServicePayload,
  type UpdateBookingSettingsPayload,
  type AvailableSlotsParams,
  type CreateSlotPayload,
} from '../api/bookingServicesApi';
import { useToast } from '../../../shared/components/toast/useToast';

const errMsg = (err: unknown, fallback: string) => {
  const msg = (err as { message?: unknown })?.message;
  if (Array.isArray(msg)) return (msg as string[])[0] || fallback;
  return (typeof msg === 'string' ? msg : '') || fallback;
};

const KEY = 'booking-services';

/**
 * List the bookable services for the given tenant. Scoping happens server-side
 * via the x-tenant-id header (set from selectedTenantId by ApiFetch); the
 * tenantId is part of the query key so the cache stays per-tenant, and the
 * query is disabled until a tenant is selected.
 */
export const useBookingServices = (tenantId: string | null | undefined) =>
  useQuery({
    queryKey: [KEY, tenantId],
    queryFn: () => bookingServicesApi.list(),
    enabled: !!tenantId,
    staleTime: 60 * 1000,
  });

export const useCreateBookingService = () => {
  const queryClient = useQueryClient();
  const { toast } = useToast();
  return useMutation({
    mutationFn: (data: CreateBookingServicePayload) => bookingServicesApi.create(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [KEY] });
      toast.success('Booking service created');
    },
    onError: (err: unknown) => toast.error(errMsg(err, 'Failed to create booking service')),
  });
};

export const useUpdateBookingService = () => {
  const queryClient = useQueryClient();
  const { toast } = useToast();
  return useMutation({
    mutationFn: ({ id, data }: { id: string; data: UpdateBookingServicePayload }) =>
      bookingServicesApi.update(id, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [KEY] });
      toast.success('Booking service updated');
    },
    onError: (err: unknown) => toast.error(errMsg(err, 'Failed to update booking service')),
  });
};

const SETTINGS_KEY = 'booking-settings';

/** Merchant booking rules (PE-2100). Scoped server-side by x-tenant-id; disabled until a merchant is selected. */
export const useBookingSettings = (tenantId: string | null | undefined) =>
  useQuery({
    queryKey: [SETTINGS_KEY, tenantId],
    queryFn: () => bookingServicesApi.getSettings(),
    enabled: !!tenantId,
    staleTime: 60 * 1000,
    // A merchant with no rules configured yet returns a 4xx/empty from booking-service;
    // treat that as "not set" in the UI rather than retrying.
    retry: false,
  });

export const useUpdateBookingSettings = (tenantId: string | null | undefined) => {
  const queryClient = useQueryClient();
  const { toast } = useToast();
  return useMutation({
    mutationFn: (data: UpdateBookingSettingsPayload) => bookingServicesApi.updateSettings(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [SETTINGS_KEY, tenantId] });
      toast.success('Booking rules saved');
    },
    onError: (err: unknown) => toast.error(errMsg(err, 'Failed to save booking rules')),
  });
};

/** Read-only computed availability for a service (PE-2100). Disabled until a service is chosen. */
export const useAvailableSlots = (params: AvailableSlotsParams | null) =>
  useQuery({
    queryKey: ['booking-slots', params?.serviceId, params?.from, params?.to],
    queryFn: () => bookingServicesApi.availableSlots(params as AvailableSlotsParams),
    enabled: !!params?.serviceId,
    staleTime: 30 * 1000,
    retry: false,
  });

const SERVICE_SLOTS_KEY = 'booking-service-slots';

/**
 * The concrete slot records (bookings + manual blocks) for one service (PE-2137).
 * Used alongside `useAvailableSlots` to grey out buffer-affected neighbours in
 * the slot picker. Disabled until a service is chosen.
 */
export const useServiceSlots = (serviceId: string | null | undefined) =>
  useQuery({
    queryKey: [SERVICE_SLOTS_KEY, serviceId],
    queryFn: () => bookingServicesApi.listServiceSlots(serviceId as string),
    enabled: !!serviceId,
    staleTime: 15 * 1000,
    retry: false,
  });

export const useCreateSlot = (serviceId: string | null | undefined) => {
  const queryClient = useQueryClient();
  const { toast } = useToast();
  return useMutation({
    mutationFn: (data: CreateSlotPayload) => bookingServicesApi.createSlot(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [SERVICE_SLOTS_KEY, serviceId] });
      queryClient.invalidateQueries({ queryKey: ['booking-slots', serviceId] });
      toast.success('Slot blocked');
    },
    onError: (err: unknown) => toast.error(errMsg(err, 'Failed to block slot')),
  });
};

export const useDeleteSlot = (serviceId: string | null | undefined) => {
  const queryClient = useQueryClient();
  const { toast } = useToast();
  return useMutation({
    mutationFn: (id: string) => bookingServicesApi.deleteSlot(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [SERVICE_SLOTS_KEY, serviceId] });
      queryClient.invalidateQueries({ queryKey: ['booking-slots', serviceId] });
      toast.success('Slot removed');
    },
    onError: (err: unknown) => toast.error(errMsg(err, 'Failed to remove slot')),
  });
};
